// src/modules/smelter/pages/SmelterGstLedger.jsx
// Per-smelter GST recovery ledger. The smelter pays us a GST-inclusive total; the
// GST inside it must be deposited ("filed") with the govt and is returned/adjusted
// by the smelter on a later transaction. This screen tracks charged vs filed vs
// returned vs outstanding per smelter, and lets you record returns + mark filings.

import React, { useEffect, useState } from 'react';
import {
  Card, Typography, Table, Row, Col, Statistic, Button, Drawer, Tag, Space, Modal,
  Form, InputNumber, Input, DatePicker, message, Popconfirm, Tooltip, Divider, Select, Segmented, Alert, Checkbox,
} from 'antd';
import {
  ReloadOutlined, PlusOutlined, CheckCircleOutlined, DeleteOutlined, RightOutlined, FileAddOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  fetchGstLedgerSummaries, fetchSmelterGstLedger, fetchGstLinkableDispatches,
  addGstCharged, addGstReturn, markGstEntryFiled, deleteGstReturn,
} from '../api';
import { usePermissions } from '../../../hooks/usePermissions';

const { Title, Text } = Typography;

const fmt = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(Number(v) || 0);

export default function SmelterGstLedger() {
  const { canEditModule } = usePermissions();
  const canEdit = canEditModule('smelter');

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ charged: 0, filed: 0, unfiled: 0, returned: 0, outstanding: 0 });
  const [messageApi, contextHolder] = message.useMessage();

  // Drawer (single smelter ledger)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [active, setActive] = useState(null); // { smelterId, smelterName }
  const [ledger, setLedger] = useState(null); // { smelter, entries, summary }
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [dispatches, setDispatches] = useState([]); // linkable dispatches for pickers

  // Add-return / add-charged modals
  const [returnModal, setReturnModal] = useState(false);
  const [returnForm] = Form.useForm();
  const [chargedModal, setChargedModal] = useState(false);
  const [chargedForm] = Form.useForm();
  const [chargedMode, setChargedMode] = useState('DISPATCH'); // DISPATCH | MANUAL
  const [busy, setBusy] = useState('');

  const dispatchOptions = dispatches.map((d) => ({
    value: d._id,
    label: `${d.dispatchId} · GST ${fmt(d.gstAmountFromSmelter || 0)}${d.dispatchDate ? ` · ${dayjs(d.dispatchDate).format('DD-MM-YYYY')}` : ''}`,
    gst: d.gstAmountFromSmelter || 0,
    date: d.dispatchDate,
    dispatchId: d.dispatchId,
  }));

  const loadSummaries = () => {
    setLoading(true);
    fetchGstLedgerSummaries()
      .then((r) => { setRows(r.data || []); setTotals(r.totals || totals); })
      .catch(() => messageApi.error('Failed to load GST ledger'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadSummaries(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openLedger = (smelterId, smelterName) => {
    setActive({ smelterId, smelterName });
    setDrawerOpen(true);
    setLedgerLoading(true);
    setDispatches([]);
    fetchSmelterGstLedger(smelterId)
      .then((r) => setLedger(r.data))
      .catch(() => messageApi.error('Failed to load smelter ledger'))
      .finally(() => setLedgerLoading(false));
    fetchGstLinkableDispatches(smelterId)
      .then((r) => setDispatches(r.data || []))
      .catch(() => setDispatches([]));
  };

  const refreshLedger = () => {
    if (!active) return;
    fetchSmelterGstLedger(active.smelterId).then((r) => setLedger(r.data)).catch(() => {});
    loadSummaries();
  };

  const submitReturn = async (values) => {
    setBusy('return');
    try {
      await addGstReturn(active.smelterId, {
        amount: values.amount,
        date: values.date?.toISOString(),
        reference: values.reference,
        invoiceNo: values.invoiceNo,
        notes: values.notes,
      });
      messageApi.success('GST return recorded');
      setReturnModal(false);
      returnForm.resetFields();
      refreshLedger();
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to record return');
    } finally { setBusy(''); }
  };

  const submitCharged = async (values) => {
    setBusy('charged');
    try {
      await addGstCharged(active.smelterId, {
        amount: values.amount,
        date: values.date?.toISOString(),
        invoiceNo: values.invoiceNo,
        dispatchId: chargedMode === 'DISPATCH' ? values.dispatchId : undefined,
        alreadyFiled: !!values.alreadyFiled,
        notes: values.notes,
      });
      messageApi.success('GST charged entry added');
      setChargedModal(false);
      chargedForm.resetFields();
      refreshLedger();
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to add entry');
    } finally { setBusy(''); }
  };

  // Auto-fill amount/invoice when a dispatch is picked in either modal.
  const onPickDispatch = (form) => (val) => {
    const opt = dispatchOptions.find((o) => o.value === val);
    if (opt) form.setFieldsValue({ amount: opt.gst || undefined, invoiceNo: form.getFieldValue('invoiceNo') || opt.dispatchId });
  };

  const toggleFiled = async (entry) => {
    setBusy(entry._id);
    try {
      await markGstEntryFiled(entry._id, { filedStatus: entry.filedStatus === 'FILED' ? 'PENDING' : 'FILED' });
      refreshLedger();
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to update');
    } finally { setBusy(''); }
  };

  const removeReturn = async (entry) => {
    setBusy(entry._id);
    try {
      await deleteGstReturn(entry._id);
      messageApi.success('Return deleted');
      refreshLedger();
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to delete');
    } finally { setBusy(''); }
  };

  const summaryColumns = [
    { title: 'Smelter', dataIndex: 'smelterName', key: 'name',
      render: (v, r) => <Button type="link" style={{ padding: 0 }} onClick={() => openLedger(r.smelterId, v)}>{v}</Button> },
    { title: 'Invoices', dataIndex: 'invoiceCount', key: 'inv', align: 'center', width: 90 },
    { title: 'GST Charged', dataIndex: 'charged', key: 'charged', align: 'right', render: fmt },
    { title: 'Filed', dataIndex: 'filed', key: 'filed', align: 'right', render: (v) => <Text type="secondary">{fmt(v)}</Text> },
    { title: 'Unfiled', dataIndex: 'unfiled', key: 'unfiled', align: 'right',
      render: (v) => <Text style={{ color: v > 0 ? '#d48806' : undefined }}>{fmt(v)}</Text> },
    { title: 'Returned', dataIndex: 'returned', key: 'returned', align: 'right', render: (v) => <Text style={{ color: '#3f8600' }}>{fmt(v)}</Text> },
    { title: 'Outstanding', dataIndex: 'outstanding', key: 'outstanding', align: 'right',
      render: (v) => <Text strong style={{ color: v > 0 ? '#cf1322' : '#3f8600' }}>{fmt(v)}</Text> },
    { title: '', key: 'go', width: 40, render: (_, r) => <Button type="text" icon={<RightOutlined />} onClick={() => openLedger(r.smelterId, r.smelterName)} /> },
  ];

  const entryColumns = [
    { title: 'Date', dataIndex: 'date', key: 'date', width: 100, render: (v) => (v ? dayjs(v).format('DD-MM-YYYY') : '—') },
    { title: 'Type', dataIndex: 'type', key: 'type', width: 130,
      render: (t, r) => (t === 'INVOICE_GST'
        ? <Tag color="blue">GST Charged{r.source === 'FLY' ? ' (Fly)' : r.manual ? ' (Manual)' : ''}</Tag>
        : <Tag color="green">Return</Tag>) },
    { title: 'Invoice / Ref', key: 'ref', render: (_, r) => r.invoiceNo || r.reference || '—' },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right', width: 120,
      render: (v, r) => <Text strong style={{ color: r.type === 'RETURN' ? '#3f8600' : undefined }}>{r.type === 'RETURN' ? '−' : '+'}{fmt(v)}</Text> },
    { title: 'Filed', key: 'filed', width: 150, render: (_, r) => {
      if (r.type !== 'INVOICE_GST') return <Text type="secondary">—</Text>;
      const filed = r.filedStatus === 'FILED';
      return (
        <Space size={4}>
          <Tag color={filed ? 'green' : 'orange'}>{filed ? 'Filed' : 'Pending'}</Tag>
          {canEdit && (
            <Tooltip title={filed ? 'Mark as not filed' : 'Mark as filed with govt'}>
              <Button size="small" type="text" icon={<CheckCircleOutlined />} loading={busy === r._id} onClick={() => toggleFiled(r)} />
            </Tooltip>
          )}
        </Space>
      );
    } },
    { title: '', key: 'actions', width: 50, render: (_, r) => {
      const deletable = r.type === 'RETURN' || (r.type === 'INVOICE_GST' && r.manual);
      return canEdit && deletable ? (
        <Popconfirm title="Delete this entry? Any linked journal is reversed." onConfirm={() => removeReturn(r)}>
          <Button size="small" type="text" danger icon={<DeleteOutlined />} loading={busy === r._id} />
        </Popconfirm>
      ) : null;
    } },
  ];

  const s = ledger?.summary || {};

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      {contextHolder}
      <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>Smelter GST Recovery</Title>
          <Text type="secondary">GST charged inside smelter payments — deposited to govt and recovered back from the smelter.</Text>
        </Col>
        <Col><Button icon={<ReloadOutlined />} onClick={loadSummaries}>Refresh</Button></Col>
      </Row>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Total GST Charged" value={fmt(totals.charged)} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Unfiled (to deposit)" value={fmt(totals.unfiled)} valueStyle={{ color: totals.unfiled > 0 ? '#d48806' : undefined }} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Returned by Smelters" value={fmt(totals.returned)} valueStyle={{ color: '#3f8600' }} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Outstanding to Recover" value={fmt(totals.outstanding)} valueStyle={{ color: totals.outstanding > 0 ? '#cf1322' : '#3f8600' }} /></Card></Col>
      </Row>

      <Card size="small">
        <Table
          rowKey="smelterId"
          loading={loading}
          dataSource={rows}
          columns={summaryColumns}
          pagination={false}
          size="small"
          scroll={{ x: 800 }}
        />
      </Card>

      <Drawer
        title={active ? `GST Ledger — ${active.smelterName}` : 'GST Ledger'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={Math.min(760, window.innerWidth)}
        extra={canEdit && (
          <Space>
            <Button icon={<FileAddOutlined />} onClick={() => { setChargedMode('DISPATCH'); chargedForm.resetFields(); chargedForm.setFieldsValue({ date: dayjs() }); setChargedModal(true); }}>
              Add GST Charged
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { returnForm.resetFields(); returnForm.setFieldsValue({ date: dayjs(), amount: s.outstanding > 0 ? s.outstanding : undefined }); setReturnModal(true); }}>
              Record Return
            </Button>
          </Space>
        )}
      >
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={12} md={6}><Statistic title="Charged" value={fmt(s.charged)} valueStyle={{ fontSize: 16 }} /></Col>
          <Col xs={12} md={6}><Statistic title="Unfiled" value={fmt(s.unfiled)} valueStyle={{ fontSize: 16, color: s.unfiled > 0 ? '#d48806' : undefined }} /></Col>
          <Col xs={12} md={6}><Statistic title="Returned" value={fmt(s.returned)} valueStyle={{ fontSize: 16, color: '#3f8600' }} /></Col>
          <Col xs={12} md={6}><Statistic title="Outstanding" value={fmt(s.outstanding)} valueStyle={{ fontSize: 16, color: s.outstanding > 0 ? '#cf1322' : '#3f8600' }} /></Col>
        </Row>
        <Divider style={{ margin: '8px 0' }} />
        <Table
          rowKey="_id"
          loading={ledgerLoading}
          dataSource={ledger?.entries || []}
          columns={entryColumns}
          pagination={{ pageSize: 15, hideOnSinglePage: true }}
          size="small"
          scroll={{ x: 640 }}
        />
      </Drawer>

      <Modal
        title="Record GST Return / Adjustment"
        open={returnModal}
        onCancel={() => setReturnModal(false)}
        onOk={() => returnForm.submit()}
        confirmLoading={busy === 'return'}
        okText="Record"
      >
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Records an actual GST reimbursement received from the smelter. Posts Dr Bank / Cr Taxes Payable." />
        <Form form={returnForm} layout="vertical" onFinish={submitReturn}>
          <Form.Item label="Against Dispatch (optional)" name="dispatchId"
            tooltip="Link the invoice/dispatch this return settles — auto-fills the invoice no.">
            <Select
              allowClear showSearch optionFilterProp="label"
              placeholder="Select a dispatch"
              options={dispatchOptions}
              onChange={onPickDispatch(returnForm)}
            />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item label="Amount (₹)" name="amount" rules={[{ required: true, message: 'Enter amount' }, { type: 'number', min: 0.01, message: 'Must be > 0' }]}>
                <InputNumber min={0} precision={2} style={{ width: '100%' }} prefix="₹" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Date" name="date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Against Invoice No (optional)" name="invoiceNo">
            <Input placeholder="e.g. AGS-2026-00130" />
          </Form.Item>
          <Form.Item label="Reference (optional)" name="reference">
            <Input placeholder="How the smelter adjusted / returned it" />
          </Form.Item>
          <Form.Item label="Notes (optional)" name="notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Add GST Charged (past / opening entry)"
        open={chargedModal}
        onCancel={() => setChargedModal(false)}
        onOk={() => chargedForm.submit()}
        confirmLoading={busy === 'charged'}
        okText="Add"
      >
        <p style={{ color: '#888', fontSize: 12, marginTop: 0 }}>
          Add GST from a past transaction the ledger didn't capture. This sets up the receivable on the
          books (opening balance) and shows it as outstanding to recover from the smelter.
        </p>
        <Segmented
          block
          style={{ marginBottom: 12 }}
          value={chargedMode}
          onChange={(v) => setChargedMode(v)}
          options={[{ label: 'From Dispatch', value: 'DISPATCH' }, { label: 'Manual (fly/legacy)', value: 'MANUAL' }]}
        />
        <Form form={chargedForm} layout="vertical" onFinish={submitCharged}>
          {chargedMode === 'DISPATCH' && (
            <Form.Item label="Dispatch" name="dispatchId" rules={[{ required: true, message: 'Select a dispatch' }]}
              tooltip="Auto-fills the invoice no. and GST amount from the dispatch.">
              <Select showSearch optionFilterProp="label" placeholder="Select a dispatch"
                options={dispatchOptions} onChange={onPickDispatch(chargedForm)} />
            </Form.Item>
          )}
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item label="GST Amount (₹)" name="amount" rules={[{ required: true, message: 'Enter amount' }, { type: 'number', min: 0.01, message: 'Must be > 0' }]}>
                <InputNumber min={0} precision={2} style={{ width: '100%' }} prefix="₹" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Date" name="date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Invoice No" name="invoiceNo"
            rules={chargedMode === 'MANUAL' ? [{ required: true, message: 'Enter the invoice no.' }] : []}>
            <Input placeholder="e.g. AGS-2026-00120" />
          </Form.Item>
          <Form.Item name="alreadyFiled" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Checkbox>Already deposited to government (mark as filed)</Checkbox>
          </Form.Item>
          <Form.Item label="Notes (optional)" name="notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
